import { useEffect, useState } from "react";
import { Droplet, FileText, ImageIcon, Trash2 } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { cn } from "@/utils/cn";
import { useModalFormSession } from "@/hooks/useModalFormSession";
import { upperLocacaoText } from "./upperLocacaoText";
import {
  COMBUSTIVEL_LABELS,
  COMBUSTIVEL_TIPOS,
  abastecimentoFotoUrl,
  deleteAbastecimentoFoto,
  extractApiError,
  uploadAbastecimentoFoto,
  type AbastecimentoDto,
  type AbastecimentoFotoDto,
  type CombustivelTipo,
  type ContratoDto,
  type MaquinarioDto,
  type RegistroHorimetroDto,
} from "@/services/locacoes";
import { resolveDefaultMaquinaId, resolveHorimetroSugestao } from "./locacoesUtils";
import { formatNumber } from "@/utils/format";

interface ContratoContext {
  contrato: Pick<ContratoDto, "id">;
  registros: RegistroHorimetroDto[];
}

interface Props {
  open: boolean;
  editing: AbastecimentoDto | null;
  maquinarios: MaquinarioDto[];
  contratoContext?: ContratoContext | null;
  presetMaquinaId?: string | null;
  busy?: boolean;
  onClose: () => void;
  onSave: (values: {
    maquinaId: string;
    data: string;
    horimetroAtual: number;
    litros: number;
    valorTotal: number;
    tipoCombustivel: CombustivelTipo;
    observacao?: string;
  }) => Promise<AbastecimentoDto | void>;
  onDelete?: () => void;
  onMutationComplete?: () => void | Promise<void>;
}

function isPdfMime(mime: string): boolean {
  return mime.toLowerCase().includes("pdf");
}

export function ModalAbastecimento({
  open,
  editing,
  maquinarios,
  contratoContext,
  presetMaquinaId,
  busy,
  onClose,
  onSave,
  onDelete,
  onMutationComplete,
}: Props) {
  const [maquinaId, setMaquinaId] = useState("");
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [horimetroAtual, setHorimetroAtual] = useState("");
  const [litros, setLitros] = useState("");
  const [valorTotal, setValorTotal] = useState("");
  const [tipoCombustivel, setTipoCombustivel] = useState<CombustivelTipo>("diesel_s10");
  const [observacao, setObservacao] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fotoError, setFotoError] = useState<string | null>(null);
  const [fotos, setFotos] = useState<AbastecimentoFotoDto[]>([]);
  const [pendingPhotos, setPendingPhotos] = useState<Array<{ file: File; preview: string }>>([]);
  const [fotoBusy, setFotoBusy] = useState(false);
  const [pendingFotoDeleteId, setPendingFotoDeleteId] = useState<string | null>(null);

  const recordId = editing?.id ?? null;
  const selected = maquinarios.find((m) => m.id === maquinaId);
  const totalPhotos = fotos.length + pendingPhotos.length;

  const sugerirHorimetro = (maq: MaquinarioDto | undefined, mid: string) => {
    const ctx =
      contratoContext && mid
        ? { contrato: contratoContext.contrato, maquinaId: mid, registros: contratoContext.registros }
        : null;
    return String(resolveHorimetroSugestao(maq, ctx));
  };

  const formSessionKey = `${editing?.id ?? "new"}::${presetMaquinaId ?? ""}`;

  useModalFormSession(open, formSessionKey, () => {
    setError(null);
    setFotoError(null);
    if (editing) {
      setMaquinaId(editing.maquinaId);
      setData(editing.data.slice(0, 10));
      setHorimetroAtual(String(editing.horimetroAtual));
      setLitros(String(editing.litros));
      setValorTotal(String(editing.valorTotal));
      setTipoCombustivel(editing.tipoCombustivel);
      setObservacao(upperLocacaoText(editing.observacao ?? ""));
      setFotos(editing.fotos ?? []);
      setPendingPhotos([]);
    } else {
      const defaultId = resolveDefaultMaquinaId(maquinarios, presetMaquinaId);
      setMaquinaId(defaultId);
      setData(new Date().toISOString().slice(0, 10));
      setLitros("");
      setValorTotal("");
      setTipoCombustivel("diesel_s10");
      setObservacao("");
      setFotos([]);
      setPendingPhotos([]);
      const defaultMaq = maquinarios.find((m) => m.id === defaultId);
      setHorimetroAtual(defaultMaq ? sugerirHorimetro(defaultMaq, defaultId) : "");
    }
  });

  useEffect(() => {
    if (!open || !editing?.fotos?.length) return;
    setFotos(editing.fotos);
  }, [open, editing?.id, editing?.fotos?.length]);

  useEffect(() => {
    if (!open || !editing) return;
    setObservacao(upperLocacaoText(editing.observacao ?? ""));
  }, [open, editing?.id, editing?.observacao]);

  useEffect(() => {
    if (!open) return;
    return () => {
      pendingPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
    };
  }, [open, pendingPhotos]);

  const handlePickPhoto = (file: File) => {
    if (recordId) {
      void handleUploadFoto(file);
      return;
    }
    const preview = URL.createObjectURL(file);
    setPendingPhotos((prev) => [...prev, { file, preview }]);
    setFotoError(null);
  };

  const handleRemovePendingPhoto = (index: number) => {
    setPendingPhotos((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed) URL.revokeObjectURL(removed.preview);
      return next;
    });
  };

  const handleUploadFoto = async (file: File) => {
    if (!recordId) return;
    setFotoBusy(true);
    setError(null);
    try {
      const foto = await uploadAbastecimentoFoto(recordId, file);
      setFotos((prev) => [...prev, foto]);
      setFotoError(null);
      await onMutationComplete?.();
    } catch (e: unknown) {
      setError(extractApiError(e, "Erro ao enviar nota/comprovante."));
    } finally {
      setFotoBusy(false);
    }
  };

  const handleDeleteFoto = async (fotoId: string) => {
    if (!recordId) return;
    setFotoBusy(true);
    setError(null);
    try {
      await deleteAbastecimentoFoto(recordId, fotoId);
      setFotos((prev) => prev.filter((f) => f.id !== fotoId));
      await onMutationComplete?.();
    } catch (e: unknown) {
      setError(extractApiError(e, "Erro ao excluir nota/comprovante."));
    } finally {
      setFotoBusy(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setFotoError(null);
    const h = Number(horimetroAtual);
    const l = Number(litros);
    const v = Number(valorTotal) || 0;
    if (!editing && !maquinaId) {
      setError("Selecione a máquina.");
      return;
    }
    if (!Number.isFinite(h) || h < 0) {
      setError("Informe o horímetro.");
      return;
    }
    if (!Number.isFinite(l) || l <= 0) {
      setError("Informe a quantidade de litros.");
      return;
    }
    if (!Number.isFinite(v) || v <= 0) {
      setError("Informe o valor gasto no abastecimento.");
      return;
    }
    if (totalPhotos < 1) {
      setFotoError("Anexe pelo menos uma nota ou comprovante do abastecimento.");
      return;
    }

    try {
      const saved = await onSave({
        maquinaId: editing?.maquinaId ?? maquinaId,
        data,
        horimetroAtual: h,
        litros: l,
        valorTotal: v,
        tipoCombustivel,
        observacao: observacao.trim(),
      });

      const savedId = saved?.id ?? recordId;
      if (savedId && pendingPhotos.length > 0) {
        setFotoBusy(true);
        try {
          for (const p of pendingPhotos) {
            await uploadAbastecimentoFoto(savedId, p.file);
          }
          pendingPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
          setPendingPhotos([]);
        } catch (e: unknown) {
          setError(extractApiError(e, "Erro ao enviar nota/comprovante."));
          throw e;
        } finally {
          setFotoBusy(false);
        }
      }

      await onMutationComplete?.();
      onClose();
    } catch (e: unknown) {
      if (!error) setError(extractApiError(e, "Erro ao salvar abastecimento."));
    }
  };

  const title = (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50">
        <Droplet className="h-4 w-4 text-sky-600" />
      </div>
      <span className="font-bold text-slate-900">{editing ? "Editar abastecimento" : "Novo abastecimento"}</span>
    </div>
  );

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={title}
        size="md"
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <div>
              {editing && onDelete && (
                <Button type="button" variant="secondary" onClick={onDelete} disabled={busy || fotoBusy}>
                  Excluir
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={onClose} disabled={busy || fotoBusy}>
                Cancelar
              </Button>
              <Button type="button" onClick={() => void handleSubmit()} loading={busy || fotoBusy}>
                Salvar
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{error}</div>
          )}
          {!editing && maquinarios.length === 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
              Nenhuma máquina disponível neste contexto. Verifique o vínculo ao contrato ou recarregue a página.
            </div>
          )}
          {editing ? (
            <Input label="Máquina" value={editing.maquinaNome} disabled />
          ) : (
            <Select
              label="Máquina *"
              value={maquinaId}
              onChange={(e) => {
                const mid = e.target.value;
                setMaquinaId(mid);
                const m = maquinarios.find((x) => x.id === mid);
                if (m) setHorimetroAtual(sugerirHorimetro(m, mid));
              }}
              options={maquinarios.map((m) => ({ value: m.id, label: m.nome }))}
              placeholder="Selecione…"
            />
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input label="Data *" type="date" value={data} onChange={(e) => setData(e.target.value)} />
            <Select
              label="Combustível *"
              value={tipoCombustivel}
              onChange={(e) => setTipoCombustivel(e.target.value as CombustivelTipo)}
              options={COMBUSTIVEL_TIPOS.map((t) => ({ value: t, label: COMBUSTIVEL_LABELS[t] }))}
            />
          </div>
          <Input
            label="Horímetro no abastecimento *"
            type="number"
            min={0}
            step="0.1"
            value={horimetroAtual}
            onChange={(e) => setHorimetroAtual(e.target.value)}
            hint={
              selected
                ? `Leitura no momento do abastecimento. Pode ser menor que o horímetro atual da máquina (${formatNumber(selected.horimetroAtual, 1)} h).`
                : "Informe a leitura no momento do abastecimento."
            }
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Litros *"
              type="number"
              min="0.1"
              step="0.1"
              value={litros}
              onChange={(e) => setLitros(e.target.value)}
            />
            <Input
              label="Valor total (R$) *"
              type="number"
              min="0.01"
              step="0.01"
              value={valorTotal}
              onChange={(e) => setValorTotal(e.target.value)}
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Observação (opcional)
            </label>
            <textarea
              value={observacao}
              onChange={(e) => setObservacao(upperLocacaoText(e.target.value))}
              rows={3}
              maxLength={500}
              placeholder="Informe algum detalhe adicional do abastecimento, se necessário."
              className="mt-1.5 w-full resize-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-brand-300 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          <div className="space-y-2">
            <label
              className={cn(
                "text-xs font-semibold uppercase tracking-wide",
                fotoError ? "text-red-600" : "text-slate-500",
              )}
            >
              Nota / comprovante *
            </label>
            <p className="text-xs text-slate-500">
              Anexe a nota fiscal ou comprovante do abastecimento (imagem ou PDF — obrigatório).
            </p>
            {fotoError && <p className="text-xs font-medium text-red-500">{fotoError}</p>}

            <label
              className={cn(
                "inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed px-4 py-3 text-sm font-medium transition-colors",
                fotoError
                  ? "border-red-300 bg-red-50/50 text-red-700 hover:border-red-400"
                  : "border-slate-300 bg-slate-50 text-slate-700 hover:border-brand-300 hover:bg-brand-50/50",
                (fotoBusy || busy) && "pointer-events-none opacity-60",
              )}
            >
              <ImageIcon className="h-4 w-4 text-brand-600" />
              {fotoBusy ? "Enviando…" : "Adicionar nota/comprovante"}
              <input
                type="file"
                className="hidden"
                accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif,application/pdf,.pdf"
                disabled={fotoBusy || busy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handlePickPhoto(f);
                  e.target.value = "";
                }}
              />
            </label>

            {(pendingPhotos.length > 0 || fotos.length > 0) && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {fotos.map((foto) => (
                  <div key={foto.id} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                    {recordId ? (
                      <a
                        href={abastecimentoFotoUrl(recordId, foto.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex aspect-[4/3] items-center justify-center bg-slate-100"
                      >
                        {isPdfMime(foto.mimeType) ? (
                          <FileText className="h-10 w-10 text-red-500" />
                        ) : (
                          <img
                            src={abastecimentoFotoUrl(recordId, foto.id)}
                            alt={foto.originalName}
                            className="h-full w-full object-cover"
                          />
                        )}
                      </a>
                    ) : (
                      <div className="flex aspect-[4/3] items-center justify-center bg-slate-100">
                        <FileText className="h-8 w-8 text-slate-400" />
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-1 border-t border-slate-100 px-2 py-1.5">
                      <p className="min-w-0 truncate text-xs text-slate-600" title={foto.originalName}>
                        {foto.originalName}
                      </p>
                      {recordId && (
                        <button
                          type="button"
                          className="shrink-0 rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                          disabled={fotoBusy}
                          onClick={() => setPendingFotoDeleteId(foto.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {pendingPhotos.map((p, i) => (
                  <div key={`pending-${i}`} className="overflow-hidden rounded-lg border border-dashed border-brand-200 bg-brand-50/30">
                    <div className="flex aspect-[4/3] items-center justify-center overflow-hidden bg-slate-100">
                      {p.file.type === "application/pdf" ? (
                        <FileText className="h-10 w-10 text-red-500" />
                      ) : (
                        <img src={p.preview} alt={p.file.name} className="h-full w-full object-cover" />
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-1 border-t border-slate-100 px-2 py-1.5">
                      <p className="min-w-0 truncate text-xs text-slate-600" title={p.file.name}>
                        {p.file.name}
                      </p>
                      <button
                        type="button"
                        className="shrink-0 rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                        disabled={fotoBusy}
                        onClick={() => handleRemovePendingPhoto(i)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={pendingFotoDeleteId != null}
        title="Excluir comprovante"
        message="Excluir esta nota/comprovante do abastecimento?"
        confirmLabel="Excluir"
        onConfirm={async () => {
          const fotoId = pendingFotoDeleteId;
          setPendingFotoDeleteId(null);
          if (fotoId) await handleDeleteFoto(fotoId);
        }}
        onCancel={() => setPendingFotoDeleteId(null)}
        loading={fotoBusy}
      />
    </>
  );
}
